import React, { useState, useEffect, useCallback } from 'react';
import { BarChart } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useData } from '../contexts/DataContext';
import { getUserStatisticsUrl, getModelTestAttemptsUrl } from '../utils/UrlUtilities';
import { QUIZ_TYPE_LABELS, isLanguageLevel, isModelTestLevel } from '../utils/Constants';
import useLocalStorageLevel from '../contexts/levelHook';
import useEntitlement from '../utils/useEntitlement';
import UpgradeNudge from './UpgradeNudge';
import YearPicker from './ui/YearPicker';

// Breakdown tables shown below the overall card. `label` is the column header /
// section title; `format` maps a raw row key to a friendly label.
const BREAKDOWN_SECTIONS = [
    { key: 'by_subject', label: 'Subject' },
    { key: 'by_topic', label: 'Topic' },
    { key: 'by_difficulty', label: 'Difficulty' },
    { key: 'by_quiz_type', label: 'Test Type', format: (k) => QUIZ_TYPE_LABELS[k] || k },
    { key: 'by_previous_year', label: 'Previous-Year Exam' },
];

const getAccuracyColor = (accuracy) => {
    if (accuracy >= 75) return 'text-success'; // green
    if (accuracy >= 60) return 'text-warning'; // yellow
    return 'text-danger'; // red
};

// Background shade for a heatmap cell by accuracy (green/yellow/red; grey when no data).
const getHeatCellStyle = (accuracy, attempted) => {
    if (!attempted) return { backgroundColor: '#f1f5f9', color: '#94a3b8' };
    if (accuracy >= 75) return { backgroundColor: '#bbf7d0', color: '#14532d' };
    if (accuracy >= 60) return { backgroundColor: '#fef08a', color: '#713f12' };
    return { backgroundColor: '#fecaca', color: '#7f1d1d' };
};

const UserStatistics = () => {
    const CallApi = useApiCaller();
    // const { dataState } = useData();
    // const level = localStorage.getItem('level') || "";
    const [level, setLevel, levelExtra] = useLocalStorageLevel();
    const { division: currentDivision, unit: currentUnit } = levelExtra;
    // Free users are limited to the recent window (server clamps too); paid = full history.
    const { isPaid } = useEntitlement();

    const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [statisticsData, setStatisticsData] = useState(null);
    // Unified sectioned model-test attempts envelope ({ metric, total_label, note, attempts }) for
    // IELTS/TOEFL/GRE/SAT — null when the level isn't a model-test level or nothing is loaded yet.
    const [modelTestAttempts, setModelTestAttempts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatistics = useCallback(async () => {
        if (!level) {
            setError("Please select a study level from Homepage to view statistics.");
            setStatisticsData(null);
            return;
        }

        setLoading(true);
        setError(null);
        setStatisticsData(null);

        try {
            const queryParams = { level };
            if (currentDivision) queryParams.division = currentDivision;
            if (currentUnit) queryParams.unit = currentUnit;
            if (selectedTimePeriod) {
                queryParams.time = selectedTimePeriod;
            } else if (selectedYear) {
                queryParams.year = parseInt(selectedYear, 10);
            }

            const url = getUserStatisticsUrl(queryParams);
            const response = await CallApi({ url, method: 'GET', payload: null, token: true });

            if (response.success) {
                setStatisticsData(response.data);
            } else {
                setError(response.message || 'Failed to fetch statistics.');
            }

            // For sectioned/scaled model-test levels (IELTS/TOEFL/GRE/SAT), also pull the per-section
            // model-test attempts — the meaningful "proper" view for a sectioned exam. The response
            // envelope carries which metric to feature + the total label + footer note per level.
            if (isModelTestLevel(level)) {
                try {
                    const attemptsRes = await CallApi({ url: getModelTestAttemptsUrl(level), method: 'GET', token: true });
                    setModelTestAttempts(attemptsRes.success ? attemptsRes.data : null);
                } catch (e) {
                    setModelTestAttempts(null);
                }
            } else {
                setModelTestAttempts(null);
            }
        } catch (err) {
            setError('An unexpected error occurred while fetching statistics.');
            console.error("Statistics fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [level, currentDivision, currentUnit, selectedTimePeriod, selectedYear]);

    useEffect(() => {
        if (level) {
            fetchStatistics();
        }
    }, [level]);

    const handleApplyFilters = () => {
        fetchStatistics();
    };

    const handleClearFilters = () => {
        setSelectedTimePeriod('');
        setSelectedYear('');
        setTimeout(fetchStatistics, 0);
    };

    const handleTimePeriodChange = (e) => {
        const value = e.target.value;
        setSelectedTimePeriod(value);
        setSelectedYear('');
    };

    if (!level) {
        return (
            <div className="container py-4">
                <div className="alert alert-danger text-center mb-4">
                    Please select a study level. You can do this on the Home page.
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <h1 className="fs-2 fw-bold text-dark mb-4 d-flex align-items-center">
                <BarChart className="me-2" size={32} /> User Performance Statistics for Level: 
                <span className="text-primary ms-2">{level}</span>
            </h1>

            {/* Filter UI */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title text-dark mb-3">Filter Statistics</h5>
                    <div className="row g-3 align-items-start">
                        <div className="col-md-6 col-lg-4">
                            <label className="form-label text-gray-700">Time Period</label>
                            <select
                                className="form-select"
                                value={selectedTimePeriod}
                                onChange={handleTimePeriodChange}
                                disabled={!!selectedYear}
                            >
                                <option value="">No Specific Time Period Selection</option>
                                <option value="last_3_months">Last 3 Months</option>
                                {isPaid && <option value="last_6_months">Last 6 Months</option>}
                                {isPaid && <option value="last_1_year">Last 1 Year</option>}
                            </select>
                            {!isPaid && (
                                <div className="mt-2 d-flex align-items-center gap-2">
                                    <small className="text-muted">Free plan shows the last 3 months.</small>
                                    <UpgradeNudge suffix="for full analytics" />
                                </div>
                            )}
                        </div>

                        {isPaid && (
                        <div className="col-md-6 col-lg-4">
                            <label className="form-label text-gray-700">Specific Year</label>
                            <YearPicker
                                value={selectedYear}
                                onChange={y => { setSelectedYear(y); setSelectedTimePeriod(''); }}
                                minYear={2025}
                                maxYear={2035}
                                placeholder="No Specific Year Selection"
                                disabled={!!selectedTimePeriod}
                            />
                        </div>
                        )}
                    </div>

                    {/* Actions: right-justified below the filter fields. */}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button onClick={handleClearFilters} className="btn btn-outline-secondary">
                            Clear Filters
                        </button>
                        <button onClick={handleApplyFilters} className="btn btn-success">
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-secondary mt-2">Loading statistics...</p>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="alert alert-danger text-center">{error}</div>
            )}

            {/* Data Display */}
            {!loading && !error && statisticsData && (
                <div className="row g-4">
                    {/* Overall Performance */}
                    <div className="col-md-12">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <h5 className="card-title text-dark mb-3">Overall Performance</h5>
                                <div className="d-flex justify-content-between text-center flex-wrap w-100">
                                    <p className="mb-0">Total Answered: <strong>{statisticsData.total_answered}</strong></p>
                                    <p className="mb-0 text-success">Correct: <strong>{statisticsData.total_correct}</strong></p>
                                    <p className="mb-0 text-danger">Incorrect: <strong>{statisticsData.total_incorrect}</strong></p>
                                    <p className="mb-0">
                                        Accuracy: <strong className={getAccuracyColor(statisticsData.total_answered ? (statisticsData.total_correct / statisticsData.total_answered) * 100 : 0)}>
                                            {statisticsData.total_answered ? ((statisticsData.total_correct / statisticsData.total_answered) * 100).toFixed(2) : '0.00'}%
                                        </strong>
                                    </p>
                                </div>
                                <div className="alert alert-info small mt-3 mb-0 py-2">
                                    <strong>Note:</strong> Questions you did not answer are not counted in this performance evaluation — only answered questions contribute to accuracy and the breakdowns below.
                                </div>
                                {isLanguageLevel(level) && (
                                    <p className="text-muted small mt-3 mb-0">
                                        For {level}, only auto-scored items count here (Reading, Listening, and Build-a-Sentence).
                                        Writing (email/discussion) and Speaking are practice-only and are not scored, so they don't appear in these numbers.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sectioned model-test attempts (IELTS/TOEFL/GRE/SAT): per-section featured
                        metric (scaled / band / raw) + a level-appropriate total column. */}
                    {isModelTestLevel(level) && modelTestAttempts && (modelTestAttempts.attempts || []).length > 0 && (() => {
                        const metric = modelTestAttempts.metric; // 'scaled' | 'band' | 'raw'
                        const attempts = modelTestAttempts.attempts || [];
                        // Feature the level's metric per section; raw/max shown as subtext (except
                        // GRE, whose featured value IS raw/max so there's no separate subtext).
                        const renderSectionCell = (s) => {
                            if (s.unscored) return <span className="text-muted">n/a</span>;
                            if (metric === 'raw') {
                                return <div className="fw-bold">{s.raw}/{s.max}</div>;
                            }
                            const value = metric === 'band' ? s.band : s.scaled;
                            return (
                                <>
                                    <div className="fw-bold">{value != null ? value : '—'}</div>
                                    <div className="small text-muted">{s.raw}/{s.max}</div>
                                </>
                            );
                        };
                        const renderTotalCell = (a) => {
                            if (metric === 'band') return a.total_band != null ? a.total_band : '—';
                            if (metric === 'raw') return `${a.total_raw}/${a.total_max}`;
                            return a.total_scaled != null ? a.total_scaled : '—';
                        };
                        return (
                        <div className="col-md-12">
                            <div className="card shadow-sm">
                                <div className="card-body">
                                    <h5 className="card-title text-dark mb-3">Model Test Attempts</h5>
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Test</th>
                                                    <th>Date</th>
                                                    {(attempts[0].sections || []).map((s) => (
                                                        <th key={s.name} className="text-center">{s.name}</th>
                                                    ))}
                                                    <th className="text-center">{modelTestAttempts.total_label}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {attempts.map((a) => (
                                                    <tr key={a.submission_id}>
                                                        <td>{a.quiz_title}</td>
                                                        <td className="small text-muted">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '—'}</td>
                                                        {(a.sections || []).map((s) => (
                                                            <td key={s.name} className="text-center">
                                                                {renderSectionCell(s)}
                                                            </td>
                                                        ))}
                                                        <td className="text-center fw-bold text-primary">{renderTotalCell(a)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {modelTestAttempts.note && (
                                        <p className="text-muted small mt-2 mb-0">{modelTestAttempts.note}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        );
                    })()}

                    {/* Tables for by_subject, by_topic, by_difficulty, by_quiz_type */}
                    {BREAKDOWN_SECTIONS
                        .filter(({ key }) => !(isLanguageLevel(level) && key === 'by_previous_year'))
                        .map(({ key, label, format }) => (
                        <div className="col-md-12" key={key}>
                            <div className="card shadow-sm">
                                <div className="card-body">
                                    <h5 className="card-title text-dark mb-3">Performance by {label}</h5>
                                    {!statisticsData[key] || Object.keys(statisticsData[key]).length === 0 ? (
                                        <p className="text-muted">No data for this filter.</p>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>{label}</th>
                                                        <th>Correct</th>
                                                        <th>Incorrect</th>
                                                        <th>Total</th>
                                                        <th>Accuracy</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Object.entries(statisticsData[key]).map(([rowKey, data]) => {
                                                        const accuracy = data.total > 0 ? (data.correct / data.total) * 100 : 0;
                                                        return (
                                                            <tr key={rowKey}>
                                                                <td>{format ? format(rowKey) : rowKey}</td>
                                                                <td className="text-success">{data.correct}</td>
                                                                <td className="text-danger">{data.incorrect}</td>
                                                                <td>{data.total}</td>
                                                                <td className={getAccuracyColor(accuracy)}>{accuracy.toFixed(2)}%</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Weakness heatmap (G7): per-subject → topics, shaded by accuracy. */}
                    <div className="col-md-12">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <h5 className="card-title text-dark mb-1">Weakness Heatmap</h5>
                                <p className="text-muted small mb-3">
                                    Topics are shaded by accuracy (weakest first) so you can spot where to focus.
                                    <span className="ms-2"><span className="badge" style={{ backgroundColor: '#fecaca', color: '#7f1d1d' }}>&lt;60%</span></span>
                                    <span className="ms-1"><span className="badge" style={{ backgroundColor: '#fef08a', color: '#713f12' }}>60–74%</span></span>
                                    <span className="ms-1"><span className="badge" style={{ backgroundColor: '#bbf7d0', color: '#14532d' }}>≥75%</span></span>
                                </p>
                                {!statisticsData.heatmap || statisticsData.heatmap.length === 0 ? (
                                    <p className="text-muted">No data for this filter.</p>
                                ) : (
                                    statisticsData.heatmap.map((row) => (
                                        <div key={row.subject} className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <strong>{row.subject}</strong>
                                                <span className={`small ${getAccuracyColor(row.accuracy)}`}>
                                                    {row.correct}/{row.attempted} · {row.accuracy.toFixed(1)}%
                                                </span>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {row.topics.map((t) => (
                                                    <div
                                                        key={t.topic}
                                                        className="rounded px-2 py-1 small"
                                                        style={{ ...getHeatCellStyle(t.accuracy, t.attempted), minWidth: 96 }}
                                                        title={`${t.topic}: ${t.correct}/${t.attempted} correct`}
                                                    >
                                                        <div className="fw-semibold text-truncate" style={{ maxWidth: 160 }}>{t.topic}</div>
                                                        <div>{t.accuracy.toFixed(0)}% · {t.correct}/{t.attempted}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserStatistics;
